import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Route } from "@corpuscomplete/web/routes/_authenticated/join/$token";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Page, PageBody, PageContent } from "@corpuscomplete/web/components/layout";
import { BrandedLoader } from "@corpuscomplete/web/components/BrandedLoader";
import { Rpc } from "@corpuscomplete/core-ui/rpc";
import { CheckCircleIcon, XCircleIcon, UsersIcon } from "lucide-react";

type JoinState = "idle" | "joining" | "success" | "error";
type InviteType = "space" | "mealPlanningSession";

export function JoinGroupPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<JoinState>("idle");
  const [acceptedType, setAcceptedType] = useState<InviteType>("space");
  const [errorMessage, setErrorMessage] = useState("");
  const rpc = Rpc.useRpc();

  const acceptInviteMutation = useMutation(
    rpc.invite.accept.mutationOptions({
      onSuccess: (result) => {
        setAcceptedType(result.type);
        setState("success");
        setTimeout(() => {
          void navigate({
            to: result.type === "mealPlanningSession" ? "/personal/meal-plans" : "/groups",
          });
        }, 1500);
      },
      onError: (err) => {
        setState("error");
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes("expired")) {
          setErrorMessage("This invite has expired.");
        } else if (message.includes("already a member")) {
          setErrorMessage("You are already a member.");
        } else if (message.includes("maximum uses")) {
          setErrorMessage("This invite has already been used.");
        } else if (message.includes("Invalid")) {
          setErrorMessage("This invite link is invalid.");
        } else {
          setErrorMessage(message);
        }
      },
    }),
  );

  const handleJoin = () => {
    setState("joining");
    acceptInviteMutation.mutate({ token });
  };

  return (
    <Page>
      <PageBody>
        <PageContent>
          <div className="flex flex-1 flex-col items-center justify-center gap-6">
            {state === "idle" && (
              <>
                <div className="flex size-20 items-center justify-center rounded-full bg-muted">
                  <UsersIcon className="size-10 text-muted-foreground" />
                </div>
                <div className="flex flex-col items-center gap-2 text-center">
                  <Text variant="lg" className="font-medium">
                    You've been invited to join
                  </Text>
                  <Text variant="sm" className="text-muted-foreground">
                    Click below to accept the invitation.
                  </Text>
                </div>
                <Button size="lg" onClick={handleJoin}>
                  Accept Invite
                </Button>
              </>
            )}

            {state === "joining" && (
              <>
                <BrandedLoader className="h-44 w-44" />
                <Text variant="sm" className="-mt-3 text-muted-foreground">
                  Accepting invite...
                </Text>
              </>
            )}

            {state === "success" && (
              <>
                <div className="flex size-20 items-center justify-center rounded-full bg-green-100">
                  <CheckCircleIcon className="size-10 text-green-600" />
                </div>
                <div className="flex flex-col items-center gap-2 text-center">
                  <Text variant="lg" className="font-medium">
                    {acceptedType === "mealPlanningSession"
                      ? "Successfully joined planning session!"
                      : "Successfully joined!"}
                  </Text>
                  <Text variant="sm" className="text-muted-foreground">
                    Redirecting to{" "}
                    {acceptedType === "mealPlanningSession" ? "meal plans" : "the group"}...
                  </Text>
                </div>
              </>
            )}

            {state === "error" && (
              <>
                <div className="flex size-20 items-center justify-center rounded-full bg-red-100">
                  <XCircleIcon className="size-10 text-red-600" />
                </div>
                <div className="flex flex-col items-center gap-2 text-center">
                  <Text variant="lg" className="font-medium">
                    Unable to accept invite
                  </Text>
                  <Text variant="sm" className="text-muted-foreground">
                    {errorMessage}
                  </Text>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate({ to: "/home" })}>
                    Go Home
                  </Button>
                  <Button
                    onClick={() => {
                      setState("idle");
                      setErrorMessage("");
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              </>
            )}
          </div>
        </PageContent>
      </PageBody>
    </Page>
  );
}
